import React, { useMemo, useCallback } from "react";
import { TableCartItemCard } from "@dishopsaas/design-system";
import {
  displayPriceFormat,
  getTotalPrice,
  updateProductInPendingOrder,
} from "../../utils";
import { getTooltipTextFromOffer } from "../../../src/components/ListProduct/components/Product/Product.services";
import _ from "lodash";
import { useAppSelector } from "../../redux/hook";
import { Divider } from "@dishopsaas/design-system";

interface CartItem {
  productKey: string;
  sectionKey: string;
  image: string;
  name: string;
  items: any[];
  nb: number;
  isLoyaltyProduct?: boolean;
  productMarketingCampaignKey?: string;
  unitPrice?: number;
  price?: {
    productPrice?: number;
    fullPrice?: number;
    productPromotionAmount?: number;
  };
}

interface CartItemsListProps {
  loading?: boolean;
  showImage?: boolean;
  showDivider?: boolean;
  showCategoryName?: boolean;
}

export default function CartItemsList({
  loading,
  showDivider = true,
  showImage = false,
  showCategoryName = true,
}: CartItemsListProps) {
  const { pendingOrderReducer, offerReducer } = useAppSelector((state) => ({
    pendingOrderReducer: state.pendingOrderReducer,
    offerReducer: state.offerReducer,
  }));

  const { commande } = pendingOrderReducer;
  const { thresholdOffer, productOffers } = offerReducer;

  const updateProductInCart = useCallback(
    async (key: string, product: CartItem, newQuantity: number) => {
      if (!product || newQuantity == null) return;

      const nb = Math.max(0, newQuantity);
      const updatedProduct = {
        ...product,
        nb,
        price: getTotalPrice(
          nb,
          product.isLoyaltyProduct ? 0 : product.unitPrice,
          product.sectionKey,
          product.items,
        ),
      };

      await updateProductInPendingOrder(
        pendingOrderReducer,
        key,
        updatedProduct,
        null,
      );
    },
    [pendingOrderReducer],
  );

  const itemsWithOffers = useMemo(() => {
    if (!commande) return [];

    return Object.entries(commande).map(([key, item]) => {
      const cartItem = item as CartItem;
      const productOffer = cartItem.productMarketingCampaignKey
        ? _.find(
          productOffers,
          (offer: any) => offer.key === cartItem.productMarketingCampaignKey,
        )
        : null;

      return {
        key,
        item: cartItem,
        badgeText: getTooltipTextFromOffer(productOffer)?.title ?? null,
      };
    });
  }, [commande, thresholdOffer, productOffers]);
  const hasImageColumn = useMemo(() => {
    if (!showImage) return false;

    return itemsWithOffers.some(({ item }) => !!item.image);
  }, [itemsWithOffers, showImage]);

  return (
    <>
      {itemsWithOffers.map(({ key, item, badgeText }) => {
        const finalPriceValue =
          (item.price?.fullPrice ?? 0) -
          (item.price?.productPromotionAmount ?? 0);

        const originalPriceValue = item.price?.fullPrice;

        const hasRealDiscount = originalPriceValue > finalPriceValue;
        const hasValidOffer = !!badgeText;
        return (
          <React.Fragment key={key}>
            <TableCartItemCard
              showCategoryName={showCategoryName}
              badgeText={hasValidOffer ? badgeText : null}
              title={item.name}
              items={item.items}
              quantity={item.nb}
              showHeader={false}
              imageSrc={showImage ? item.image : undefined}
              hasImageColumn={hasImageColumn}
              originalPrice={
                hasRealDiscount
                  ? displayPriceFormat(originalPriceValue, false)
                  : null
              }
              finalPrice={displayPriceFormat(finalPriceValue)}
              onQuantityChange={(q) => updateProductInCart(key, item, q)}
              loading={loading}
            />

            {showDivider && <Divider sx={{ mt: 2 }} />}
          </React.Fragment>
        );
      })}
    </>
  );
}
